import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Card, Button, Grid, Chip, Avatar } from "@mui/material";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import StarIcon from "@mui/icons-material/Star";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import BoltIcon from "@mui/icons-material/Bolt";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import ChatIcon from "@mui/icons-material/Chat";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import MicIcon2 from "@mui/icons-material/Mic";
import ExploreIcon from "@mui/icons-material/Explore";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import RepeatIcon from "@mui/icons-material/Repeat";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import StormIcon from "@mui/icons-material/Storm";
import LabelImportantIcon from "@mui/icons-material/LabelImportant";
import ConnectWithoutContactIcon from "@mui/icons-material/ConnectWithoutContact";
import LanguageIcon from "@mui/icons-material/Language";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/AuthContext";
import { api } from "../services/api";
import { getPitchTypeById } from "../services/pitchTypesService";
import { getAIProfileById } from "../services/aiProfilesService";
import AppBackground from "../components/layout/AppBackground";
import AppLoader from "../components/AppLoader";
import HomeVideoShowcase from "../components/home/HomeVideoShowcase";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CampaignIcon from "@mui/icons-material/Campaign";
import MicIcon from "@mui/icons-material/Mic";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

const cardSx = {
  backgroundColor: "rgba(20, 12, 37, 0.8)",
  borderRadius: 5,
  border: "1px solid rgba(255,255,255,0.08)",
  boxShadow: "0 18px 50px rgba(0,0,0,0.22)",
};

const achievementIconMap = {
  Star: StarIcon,
  EmojiEvents: EmojiEventsIcon,
  Whatshot: WhatshotIcon,
  Bolt: BoltIcon,
  FlashOn: FlashOnIcon,
  Chat: ChatIcon,
  RecordVoiceOver: RecordVoiceOverIcon,
  Mic: MicIcon2,
  Explore: ExploreIcon,
  TravelExplore: TravelExploreIcon,
  Repeat: RepeatIcon,
  CenterFocusStrong: CenterFocusStrongIcon,
  Storm: StormIcon,
  LabelImportant: LabelImportantIcon,
  ConnectWithoutContact: ConnectWithoutContactIcon,
  Language: LanguageIcon,
  TrackChanges: TrackChangesIcon,
  ShowChart: ShowChartIcon,
  GraphicEq: GraphicEqIcon,
};

const achievementIconColorMap = {
  info: "#2196f3",
  error: "#f44336",
  success: "#4caf50",
  warning: "#ff9800",
  purple: "#a855f7",
  violet: "#a855f7",
  primary: "#a855f7",
  secondary: "#a855f7",
  default: "#aaa",
};

function formatDaysAgo(days, t) {
  if (days === null || days === undefined) return null;
  if (days <= 0) return t("logged_last_session_today");
  if (days === 1) return t("logged_last_session_yesterday");
  return t("logged_last_session_days_ago", { days });
}

function getIconForPitchType(name = "") {
  const lower = name.toLowerCase();
  if (lower.includes("elevator")) return <TrendingUpIcon sx={{ fontSize: 18 }} />;
  if (lower.includes("demo")) return <CampaignIcon sx={{ fontSize: 18 }} />;
  if (lower.includes("media") || lower.includes("medio")) return <MicIcon sx={{ fontSize: 18 }} />;
  return <RocketLaunchIcon sx={{ fontSize: 18 }} />;
}

export default function UserHome() {
  const { t, i18n } = useTranslation("home");
  const { user, authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [pendingAchievements, setPendingAchievements] = useState([]);

  useEffect(() => {
    if (authLoading) return;

    const fetchDashboard = async () => {
      try {
        const [dashboardRes, achievementsRes] = await Promise.all([
          api.get("/api/user/dashboard"),
          api.get("/api/user/achievements"),
        ]);
        setDashboardData(dashboardRes.data);
        setPendingAchievements(achievementsRes.data?.achievements_pending || []);
      } catch (error) {
        console.error("Error loading home dashboard data:", error);
        setDashboardData(null);
        setPendingAchievements([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [authLoading]);

  const userName = useMemo(
    () => user?.first_name || user?.profile_name || "Founder",
    [user]
  );

  const langIsEs = i18n.language?.startsWith("es");
  const emptySessionPreviewImage = langIsEs
    ? "/assets/home/product-conversation-es.webp"
    : "/assets/home/product-conversation-en.webp";
  const hasRealData =
    dashboardData && dashboardData.message !== "No data available";
  const lastSession = hasRealData ? dashboardData.last_session : null;
  const metrics = hasRealData ? dashboardData.metrics : null;

  const progressItems = hasRealData
    ? [
        {
          label: t("progress_score"),
          value: dashboardData.global_score?.toFixed(1) || "—",
        },
        {
          label: t("progress_sessions"),
          value: dashboardData.session_stats?.total_sessions ?? "—",
        },
        {
          label: t("progress_words"),
          value: dashboardData.word_stats?.total_words
            ? new Intl.NumberFormat(langIsEs ? "es-ES" : "en-US").format(
                dashboardData.word_stats.total_words
              )
            : "—",
        },
        {
          label: t("progress_wpm"),
          value: dashboardData.speech_stats?.avg_wpm
            ? Math.round(dashboardData.speech_stats.avg_wpm)
            : "—",
        },
      ]
    : [];

  const metricHighlights = hasRealData
    ? [
        { label: t("progress_clarity"), value: metrics?.clarity },
        { label: t("progress_relevance"), value: metrics?.relevance },
        { label: t("progress_fluency"), value: metrics?.fluency },
        { label: t("progress_engagement"), value: metrics?.engagement },
      ]
    : [];
  const achievementsPreview = hasRealData
    ? (dashboardData.achievements || []).slice(0, 3)
    : [];
  const pendingAchievementsPreview = pendingAchievements.slice(0, 3);
  const showUnlockedAchievements = achievementsPreview.length > 0;

  const handleContinueTraining = async () => {
    if (!lastSession?.session?.pitch_type_id || !lastSession?.session?.ai_profile_id) {
      navigate("/pitch-start");
      return;
    }

    try {
      const [pitchType, aiProfile] = await Promise.all([
        getPitchTypeById(lastSession.session.pitch_type_id),
        getAIProfileById(lastSession.session.ai_profile_id),
      ]);

      if (pitchType && aiProfile) {
        navigate("/pitch-start", {
          state: { pitchType, aiProfile },
        });
        return;
      }
    } catch (error) {
      console.error("Error loading last session config:", error);
    }

    navigate("/pitch-start");
  };

  if (authLoading || loading) {
    return <AppLoader message={t("loading_logged_home")} />;
  }

  return (
    <AppBackground sx={{ pt: { xs: 2.5, md: 4 }, pb: 8 }}>
      <Box sx={{ width: "100%", maxWidth: 1080, mx: "auto" }}>
        <Card sx={{ ...cardSx, p: { xs: 2.5, md: 4 }, mb: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  mb: 1,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                }}
              >
                {t("logged_greeting", { name: userName })}
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: "2.4rem", md: "3.4rem" },
                  lineHeight: 1.02,
                  mb: 1.5,
                  maxWidth: 520,
                }}
              >
                {t("logged_title")}
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.72)",
                  fontSize: { xs: "1rem", md: "1.08rem" },
                  maxWidth: 520,
                }}
              >
                {t("logged_subtitle")}
              </Typography>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  minHeight: { xs: 220, md: 260 },
                  borderRadius: 4,
                  background:
                    "radial-gradient(circle at 55% 45%, rgba(168,85,247,0.3), transparent 35%), radial-gradient(circle at 70% 40%, rgba(217,70,239,0.22), transparent 22%), linear-gradient(135deg, rgba(46,25,76,0.78), rgba(30,18,52,0.9))",
                  border: "1px solid rgba(255,255,255,0.08)",
                  p: 2,
                  boxShadow: "0 20px 35px rgba(0,0,0,0.18)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                  {hasRealData && lastSession?.session?.id ? (
                    <>
                      <Typography sx={{ fontSize: "0.8rem", color: "#C084FC", mb: 1, fontWeight: 700 }}>
                        {t("logged_continue_label")}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          gap: 1,
                          mb: 1.25,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.1 }}>
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: "50%",
                              backgroundColor: "rgba(168,85,247,0.2)",
                              color: "#E9D5FF",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: "1px solid rgba(168,85,247,0.35)",
                              flexShrink: 0,
                            }}
                          >
                            {getIconForPitchType(
                              langIsEs
                                ? lastSession.session.pitch_type_name_es
                                : lastSession.session.pitch_type_name_en
                            )}
                          </Box>
                          <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.2rem", md: "1.35rem" } }}>
                            {langIsEs
                              ? lastSession.session.pitch_type_name_es
                              : lastSession.session.pitch_type_name_en}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.1 }}>
                          <Avatar
                            src={lastSession.session.ai_profile_image}
                            alt={
                              langIsEs
                                ? lastSession.session.ai_profile_name_es
                                : lastSession.session.ai_profile_name_en
                            }
                            sx={{
                              width: 38,
                              height: 38,
                              border: "2px solid rgba(255,255,255,0.2)",
                              flexShrink: 0,
                            }}
                          />
                          <Typography sx={{ color: "rgba(255,255,255,0.82)", fontWeight: 600 }}>
                            {langIsEs
                              ? lastSession.session.ai_profile_name_es
                              : lastSession.session.ai_profile_name_en}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography sx={{ color: "rgba(255,255,255,0.72)", mb: 1.75 }}>
                        {t("logged_continue_with", {
                          name: langIsEs
                            ? lastSession.session.ai_profile_name_es
                            : lastSession.session.ai_profile_name_en,
                        })}
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                        <Chip
                          label={formatDaysAgo(
                            dashboardData?.session_stats?.last_session_days_ago,
                            t
                          )}
                          sx={{
                            backgroundColor: "rgba(255,255,255,0.08)",
                            color: "#fff",
                          }}
                        />
                        <Chip
                          label={`${t("progress_score")}: ${dashboardData.global_score?.toFixed(1) || "—"}`}
                          sx={{
                            backgroundColor: "rgba(168,85,247,0.18)",
                            color: "#E9D5FF",
                          }}
                        />
                      </Box>
                      <Box sx={{ mt: "auto" }}>
                        <Button
                          variant="contained"
                          onClick={handleContinueTraining}
                          endIcon={<ArrowForwardIosRoundedIcon sx={{ fontSize: 16 }} />}
                          sx={{
                            borderRadius: 3,
                            px: 3,
                            py: 1.2,
                            fontWeight: 700,
                            textTransform: "none",
                            background: "linear-gradient(to right, #8b5cf6, #a855f7)",
                          }}
                        >
                          {t("logged_continue_cta")}
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Typography sx={{ fontSize: "0.8rem", color: "#C084FC", mb: 1, fontWeight: 700 }}>
                        {t("logged_start_label")}
                      </Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", lg: "0.95fr 1.2fr" },
                          gap: 2,
                          alignItems: "center",
                          mt: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            order: { xs: 2, lg: 1 },
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                            {t("hero_visual_title")}
                          </Typography>
                          <Typography
                            sx={{
                              color: "rgba(255,255,255,0.68)",
                              fontSize: "0.95rem",
                              mb: 2,
                              maxWidth: 260,
                            }}
                          >
                            {t("hero_visual_text")}
                          </Typography>
                          <Box sx={{ mt: "auto" }}>
                            <Button
                              variant="contained"
                              onClick={() => navigate("/pitch-start")}
                              startIcon={<PlayArrowRoundedIcon />}
                              sx={{
                                borderRadius: 3,
                                px: 3,
                                py: 1.2,
                                fontWeight: 700,
                                textTransform: "none",
                                background: "linear-gradient(to right, #8b5cf6, #a855f7)",
                              }}
                            >
                              {t("logged_start_cta")}
                            </Button>
                          </Box>
                        </Box>

                        <Box
                          component="img"
                          src={emptySessionPreviewImage}
                          alt={t("logged_start_title")}
                          sx={{
                            width: "100%",
                            height: { xs: 170, lg: 210 },
                            objectFit: "cover",
                            objectPosition: "center top",
                            borderRadius: 3,
                            border: "1px solid rgba(255,255,255,0.08)",
                            order: { xs: 1, lg: 2 },
                          }}
                        />
                      </Box>
                    </>
                  )}
              </Box>
            </Grid>
          </Grid>
        </Card>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Card sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, height: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {t("progress_title")}
                  </Typography>
                <Button
                  onClick={() => navigate("/dashboard")}
                  endIcon={<ArrowForwardIosRoundedIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    color: "#C084FC",
                    fontWeight: 700,
                    textTransform: "none",
                    minWidth: 0,
                    p: 0,
                  }}
                >
                  {t("progress_cta")}
                </Button>
              </Box>

              {hasRealData ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1.05fr 1fr" },
                    gap: 2.5,
                    alignItems: "start",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, 1fr)",
                      gap: 1.5,
                      alignSelf: "start",
                    }}
                  >
                    {progressItems.map((item) => (
                      <Box
                        key={item.label}
                        sx={{
                          backgroundColor: "rgba(255,255,255,0.04)",
                          borderRadius: 3,
                          p: 1.5,
                          minHeight: 92,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography sx={{ color: "rgba(255,255,255,0.66)", fontSize: "0.9rem" }}>
                          {item.label}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
                    {metricHighlights.map((metric) => (
                      <Box key={metric.label}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.4,
                          }}
                        >
                          <Typography sx={{ fontSize: "0.95rem" }}>{metric.label}</Typography>
                          <Typography sx={{ fontWeight: 700 }}>
                            {metric.value?.toFixed(1) || "—"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            height: 8,
                            borderRadius: 999,
                            backgroundColor: "rgba(255,255,255,0.08)",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              width: `${Math.min(((metric.value || 0) / 5) * 100, 100)}%`,
                              height: "100%",
                              background:
                                "linear-gradient(to right, #8b5cf6, #c084fc)",
                            }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    minHeight: 180,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <ShowChartRoundedIcon sx={{ color: "#C084FC", fontSize: 34, mb: 1.25 }} />
                  <Typography sx={{ color: "rgba(255,255,255,0.72)" }}>
                    {t("progress_empty_text")}
                  </Typography>
                </Box>
              )}
            </Card>
          </Grid>
        </Grid>

        <HomeVideoShowcase />

        <Card sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mt: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EmojiEventsRoundedIcon sx={{ color: "#FACC15" }} />
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {showUnlockedAchievements
                  ? t("achievements_title")
                  : t("achievements_pending_title")}
              </Typography>
            </Box>
            <Button
              onClick={() => navigate("/achievements")}
              endIcon={<ArrowForwardIosRoundedIcon sx={{ fontSize: 14 }} />}
              sx={{
                color: "#C084FC",
                fontWeight: 700,
                textTransform: "none",
                minWidth: 0,
                p: 0,
              }}
            >
              {t("achievements_cta")}
            </Button>
          </Box>

          {(showUnlockedAchievements
            ? achievementsPreview
            : pendingAchievementsPreview
          ).length > 0 ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                gap: 1.5,
              }}
            >
              {(showUnlockedAchievements
                ? achievementsPreview
                : pendingAchievementsPreview
              ).map((achievement) => (
                (() => {
                  const MuiIcon = achievement.icon_mui
                    ? achievementIconMap[achievement.icon_mui]
                    : null;
                  const rawIconColor = (achievement.icon_color || "").trim();
                  const normalizedIconColor = rawIconColor.toLowerCase();
                  const iconColor =
                    achievementIconColorMap[normalizedIconColor] ||
                    rawIconColor ||
                    achievementIconColorMap.default;

                  return (
                    <Box
                      key={achievement.id}
                      sx={{
                        backgroundColor: showUnlockedAchievements ? "rgba(255,255,255,0.04)" : "#1c1c1c",
                        borderRadius: 3,
                        border: "1px solid rgba(255,255,255,0.08)",
                        p: 1.5,
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        textAlign: "left",
                        gap: 1.25,
                        opacity: showUnlockedAchievements ? 1 : 0.5,
                        boxShadow: showUnlockedAchievements
                          ? "none"
                          : "0 0 8px rgba(255, 255, 255, 0.05)",
                        transition: "none",
                      }}
                    >
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: "50%",
                          backgroundColor: "rgba(255,255,255,0.06)",
                          color: iconColor,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {MuiIcon ? (
                          <MuiIcon sx={{ fontSize: 22, color: iconColor }} />
                        ) : (
                          <Typography sx={{ fontSize: "1.1rem", lineHeight: 1 }}>
                            {achievement.icon || "🏅"}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, lineHeight: 1.2, mb: 0.25 }}>
                          {langIsEs ? achievement.name_es : achievement.name_en}
                        </Typography>
                        <Typography
                          sx={{
                            color: "rgba(255,255,255,0.66)",
                            fontSize: "0.85rem",
                            lineHeight: 1.25,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {langIsEs
                            ? achievement.description_es
                            : achievement.description_en}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })()
              ))}
            </Box>
          ) : (
            <Typography sx={{ color: "rgba(255,255,255,0.72)" }}>
              {showUnlockedAchievements
                ? t("achievements_empty")
                : t("achievements_pending_empty")}
            </Typography>
          )}
        </Card>
      </Box>
    </AppBackground>
  );
}

